import { useState } from "react"
import { Button, Input, Dialog, DialogContent, DialogHeader, DialogTitle } from "@aix/ui"
import { trpc } from "~/lib/trpc"
import toast from "react-hot-toast"

interface CreateCoachModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

export default function CreateCoachModal({ isOpen, onClose, onSuccess }: CreateCoachModalProps) {
  const [name, setName] = useState("")
  const [icon, setIcon] = useState("🏃")
  const [description, setDescription] = useState("")
  const [type, setType] = useState<"pillar" | "side-quest">("pillar")
  const [tone, setTone] = useState<"tough-love" | "chill" | "science-y">("chill")
  const [targetEndDate, setTargetEndDate] = useState("")

  const createMutation = trpc.coaches.create.useMutation({
    onSuccess: () => {
      toast.success("Coach created successfully!")
      resetForm()
      onSuccess()
      onClose()
    },
    onError: (error) => {
      toast.error(`Failed to create coach: ${error.message}`)
    },
  })

  const resetForm = () => {
    setName("")
    setIcon("🏃")
    setDescription("")
    setType("pillar")
    setTone("chill")
    setTargetEndDate("")
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!name.trim()) {
      toast.error("Coach name is required")
      return
    }

    if (type === "side-quest" && !targetEndDate) {
      toast.error("Side quest coaches must have a target end date")
      return
    }

    createMutation.mutate({
      name: name.trim(),
      icon,
      description: description.trim() || undefined,
      type,
      tone,
      targetEndDate: targetEndDate || undefined,
      startDate: new Date().toISOString().split("T")[0],
    })
  }

  const iconOptions = ["🏃", "💪", "🧘", "📚", "🎨", "💰", "🍎", "🏋️", "🚀", "🧠", "⚡", "🔥"]

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto border-4 border-border">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-primary">➕ Create New Coach</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Name */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-muted-foreground">
              Coach Name <span className="text-destructive">*</span>
            </label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., 'Live to 200', 'Half Marathon Training'"
              className="border-2 border-border"
              required
            />
          </div>

          {/* Icon Picker */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-muted-foreground">
              Icon <span className="text-destructive">*</span>
            </label>
            <div className="grid grid-cols-6 gap-2">
              {iconOptions.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => setIcon(emoji)}
                  className={`p-4 text-3xl border-2 border-border hover:bg-secondary transition ${
                    icon === emoji ? "bg-primary" : "bg-background"
                  }`}
                >
                  {emoji}
                </button>
              ))}
            </div>
            <Input
              type="text"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              placeholder="Or enter custom emoji"
              className="border-2 border-border mt-2"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-muted-foreground">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional: Describe what this coach helps with"
              className="w-full border-2 border-border bg-background px-3 py-2 min-h-20"
              rows={3}
            />
          </div>

          {/* Type */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-muted-foreground">
              Type <span className="text-destructive">*</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setType("pillar")}
                className={`p-4 border-2 border-border text-left ${
                  type === "pillar" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-secondary"
                }`}
              >
                <div className="font-bold">🏛️ Pillar</div>
                <div className="text-xs mt-1 opacity-80">Long-term, ongoing habit</div>
              </button>
              <button
                type="button"
                onClick={() => setType("side-quest")}
                className={`p-4 border-2 border-border text-left ${
                  type === "side-quest" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-secondary"
                }`}
              >
                <div className="font-bold">⚔️ Side Quest</div>
                <div className="text-xs mt-1 opacity-80">Time-bound project</div>
              </button>
            </div>
          </div>

          {/* Target End Date (for side-quest) */}
          {type === "side-quest" && (
            <div className="space-y-2">
              <label className="text-sm font-semibold text-muted-foreground">
                Target End Date <span className="text-destructive">*</span>
              </label>
              <Input
                type="date"
                value={targetEndDate}
                onChange={(e) => setTargetEndDate(e.target.value)}
                className="border-2 border-border"
                required
              />
            </div>
          )}

          {/* Tone */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-muted-foreground">
              Coach Tone <span className="text-destructive">*</span>
            </label>
            <div className="grid grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => setTone("tough-love")}
                className={`p-4 border-2 border-border text-left ${
                  tone === "tough-love" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-secondary"
                }`}
              >
                <div className="font-bold">💪 Tough Love</div>
                <div className="text-xs mt-1 opacity-80">Direct, no excuses</div>
              </button>
              <button
                type="button"
                onClick={() => setTone("chill")}
                className={`p-4 border-2 border-border text-left ${
                  tone === "chill" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-secondary"
                }`}
              >
                <div className="font-bold">😎 Chill</div>
                <div className="text-xs mt-1 opacity-80">Supportive, friendly</div>
              </button>
              <button
                type="button"
                onClick={() => setTone("science-y")}
                className={`p-4 border-2 border-border text-left ${
                  tone === "science-y" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-secondary"
                }`}
              >
                <div className="font-bold">🔬 Science-y</div>
                <div className="text-xs mt-1 opacity-80">Data-driven, analytical</div>
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 justify-end pt-4 border-t-2 border-border">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                resetForm()
                onClose()
              }}
              className="border-2 border-border"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-primary text-primary-foreground border-2 border-border"
              loading={createMutation.isLoading}
            >
              Create Coach
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
